// A movable window over the page: a title bar to drag it by, ✕ (or Esc) to close it, and a body for whatever
// it holds (often a panel lent by the dock, Dock.lend). It doesn't block the page behind it. Where it was put
// and its size are kept in this browser, per window id.

const KEY = 'cloudview.fwin.v1:';
let top = 50;   // z-index of the last window brought to the front

export class FloatWin {
  readonly el: HTMLElement;
  readonly body: HTMLElement;
  private head: HTMLElement;
  private label: HTMLElement;
  onClose?: () => void;

  constructor(private id: string, title: string, opts: { width?: number; onClose?: () => void } = {}) {
    this.onClose = opts.onClose;
    this.el = document.createElement('div');
    this.el.className = 'fwin';
    this.el.hidden = true;
    this.head = document.createElement('div');
    this.head.className = 'fwin-head';
    this.label = document.createElement('b');
    this.label.textContent = title;
    const x = document.createElement('button');
    x.className = 'fwin-x';
    x.textContent = '✕';
    x.title = 'close (Esc)';
    x.onclick = () => this.close();
    this.head.append(this.label, x);
    this.body = document.createElement('div');
    this.body.className = 'fwin-body';
    this.el.append(this.head, this.body);
    this.el.style.width = `${opts.width ?? 420}px`;
    this.el.addEventListener('pointerdown', () => this.front(), true);
    this.head.addEventListener('pointerdown', e => this.drag(e));
    this.el.addEventListener('keydown', e => { if (e.key === 'Escape') { e.stopPropagation(); this.close(); } });
    new ResizeObserver(() => { if (!this.el.hidden) this.save(); }).observe(this.el);
    document.body.append(this.el);
  }

  get isOpen() { return !this.el.hidden; }

  setTitle(t: string) { this.label.textContent = t; }

  open() {
    const was = this.isOpen;
    this.el.hidden = false;
    if (!was) this.restore();
    this.front();
  }

  close() {
    if (!this.isOpen) return;
    this.save();
    this.el.hidden = true;
    this.onClose?.();
  }

  front() { this.el.style.zIndex = String(++top); }

  /** Where it was left (and its size), else near the middle of the page; kept on screen either way. */
  private restore() {
    let s: { x?: number; y?: number; w?: number; h?: number } = {};
    try { s = JSON.parse(localStorage.getItem(KEY + this.id) || '{}') ?? {}; } catch { /* storage blocked */ }
    if (s.w) this.el.style.width = `${s.w}px`;
    if (s.h) this.el.style.height = `${s.h}px`;
    const w = this.el.offsetWidth;
    this.place(s.x ?? (innerWidth - w) / 2, s.y ?? Math.max(40, innerHeight * 0.15));
  }

  private place(x: number, y: number) {
    const w = this.el.offsetWidth, h = this.head.offsetHeight || 28;
    this.el.style.left = `${Math.round(Math.max(0, Math.min(x, innerWidth - Math.min(w, 120))))}px`;
    this.el.style.top = `${Math.round(Math.max(0, Math.min(y, innerHeight - h)))}px`;
  }

  private save() {
    const r = this.el.getBoundingClientRect();
    if (!r.width) return;
    try { localStorage.setItem(KEY + this.id, JSON.stringify({ x: r.left, y: r.top, w: r.width, h: this.el.style.height ? r.height : undefined })); } catch { /* storage blocked */ }
  }

  private drag(e: PointerEvent) {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    this.head.setPointerCapture(e.pointerId);
    const r = this.el.getBoundingClientRect(), dx = e.clientX - r.left, dy = e.clientY - r.top;
    this.head.onpointermove = m => this.place(m.clientX - dx, m.clientY - dy);
    this.head.onpointerup = this.head.onpointercancel = () => {
      this.head.onpointermove = this.head.onpointerup = this.head.onpointercancel = null;
      this.save();
    };
  }
}
